import React, { useEffect, useState } from "react";
import { Content } from "../../components/Layout";
import { ActionText, Box, Flex } from "../../components/Flex";
import { Paragraph, Title } from "../../components/Text";
import { Button } from "../../components/Button";
import { CloseIcon } from "../../icons/CloseIcon";
import { Scrollbar } from "../../components/Scrollbar";
import { PlusIcon } from "../../icons/PlusIcon";
import { EditDetailsIcon } from "../../icons/EditDetailsIcon";
import { RemoveIcon } from "../../icons/RemoveIcon";
import { toLower } from "lodash";
import { contextSearch, createMarkup, highlightText, noneToNull } from "../../utils";
import { SearchDetailsBox } from "./SearchDetailsBox";
import { EditIcon } from "../../icons/EditIcon";
import { RevokeIcon } from "../../icons/RevokeIcon";
import { DoneIcon } from "../../icons/DoneIcon";


const UserDetails = ({
    data={},
    loading,
    issueGrant,
    editInfo,
    revokeGrant,
    changeUserAccess,
    onClose,
    deleteUser,
    userWrite,
    currentOrg,
    isAdmin,
    renderFieldContent
}) => {
    const { disabled, email, grants=[], orgAlias } = data;
    const initialState = {
        search: false,
        context: "",
        grants: null,
        cache: null
    };
    const [ state, setState ] = useState(initialState);

    useEffect(() => {
        const { context } = state;
        if (loading === false && Array.isArray(grants)) {
            setState(state => ({
                ...state,
                grants: context.length ? contextSearch(grants, context) : grants,
                cache: grants
            }));
        };
    }, [ loading, grants ]);

    const fields = {
        general: [
            { label: "Full Name", attr: "name" },
            { label: "Email", attr: "email", render: d => <a href={`mailto: ${d}`}>{d}</a> },
            { label: "Status", attr: "disabled", render: d => d ? <span style={{ color: "var(--sp-danger-color)"}}>Disabled</span> : <span style={{ color: "var(--sp-success-color)" }}>Active</span> },
            { label: "Organization", attr: "orgName" }
        ],
        grants: [
            { label: "Organization", attr: "orgName", render: (d) => <span dangerouslySetInnerHTML={createMarkup(highlightText(d, state.context))} />},
            { label: "Organization alias", attr: "orgAlias", render: (d) => <span dangerouslySetInnerHTML={createMarkup(highlightText(d, state.context))} />},
            { label: "Grant", attr: "grantName", render: (d) => <span dangerouslySetInnerHTML={createMarkup(highlightText(d, state.context))} /> },
            { label: "Expiration Date", attr: "expiryDate", render: (d) => <span dangerouslySetInnerHTML={createMarkup(highlightText(d, state.context))} /> },
            { label: "Issued By", attr: "issuedBy", render: (d) => <span dangerouslySetInnerHTML={createMarkup(highlightText(d, state.context))} /> }
        ]
    };

    return(
        <Content background="#FFFFFF" gap={{ bottom: 21 }}>
            <Flex width="100%" justify="space-between" margin={{ bottom: "24px"}}>
                <Title h4 isLoading={loading}>
                    {data.name}
                </Title>
                <CloseIcon onClick={onClose} />
            </Flex>
            <Flex direction="column">
                <Title h6 capitalize margin={{ bottom: "7px" }}>general</Title>
                <Flex direction="column" gap={{ bottom: 4 }}>
                    {renderFieldContent(fields.general, data)}
                </Flex>
            </Flex>
            <Flex direction="column" flex={1} margin={{ right: "-17px" }}>
                <Flex width="100%" justify="space-between" margin={{ bottom: "24px" }} padding={{ right: "17px"}}>
                    <Title h6 capitalize margin={{ bottom: "7px" }}>grants ({grants.length})</Title>
                    <Flex gap={{ right: 12 }}>
                        <SearchDetailsBox
                            onExpand={() => setState(state => ({ ...state, search: true }))}
                            onCollapse={() => setState(state => ({ ...state, context: "", search: false, grants: state.cache }))}
                            onSearch={(text) => setState(state => ({
                                ...state,
                                context: text,
                                grants: text.length ? contextSearch(state.cache, text) : state.cache
                            }))}
                            visible={state.search}
                            value={state.context}
                        />
                        {issueGrant && <ActionText onClick={() => issueGrant()}>
                            <PlusIcon style={{ marginRight: "6px" }} />
                            <span>Issue Grant</span>
                        </ActionText>}
                    </Flex>
                </Flex>
                <Scrollbar>
                {loading || state.grants === null
                    ? <Box flatten isLoading />
                    : state.grants && Array.isArray(state.grants)
                        ? <Flex direction="column" gap={{ bottom: 16 }} width="calc(100% - 17px)" position="relative">
                            {state.grants.map((grant, idx) => (
                            <Box
                                flatten
                                key={`box-${idx}`}
                                background="#F0F4FB"
                                gap={{ bottom: 4 }}
                                direction="row"
                            >
                                <Flex direction="column" flex={1}>
                                    {renderFieldContent(fields.grants, grant)}
                                </Flex>
                                <Flex gap={{ right: 8 }} align="start">
                                    {grant.orgAlias !== orgAlias && userWrite &&
                                    <ActionText danger onClick={() => revokeGrant({ email: email, ...grant})}>
                                        <RemoveIcon />
                                        <span>Revoke Grant</span>
                                    </ActionText>}
                                    {userWrite &&
                                    <ActionText onClick={() => editInfo("grants", Object.assign({}, grant, { email: email, setExpiration: toLower(grant.expiryDate) !== "none", expiryDate: noneToNull(grant.expiryDate) }))}>
                                        <EditDetailsIcon />
                                        <span>Edit Grant</span>
                                    </ActionText>}
                                </Flex>
                            </Box>
                        ))}
                        </Flex>
                        : null}
                </Scrollbar>
            </Flex>
            <Flex justify="space-between">
                <Flex gap={{ right: 12 }}>
                    {deleteUser && <Button
                        danger
                        disabled={loading || disabled === undefined}
                        onClick={() => deleteUser({
                            content: <Paragraph>Are you sure you want to delete the <b>{email}</b> without the possibility of recovery?</Paragraph>,
                            id: email
                        })}
                    >
                        <RemoveIcon size={20} />
                        Delete User
                    </Button>}
                    {changeUserAccess && <Button
                        danger={!disabled}
                        disabled={loading || disabled === undefined}
                        onClick={() => changeUserAccess(email, disabled)}
                    >
                        {disabled ? <DoneIcon size={20} /> : <RevokeIcon size={20} />}
                        {disabled ? "Enable" : "Disable"} Access
                    </Button>}
                    {userWrite && (isAdmin || orgAlias == currentOrg) &&
                    <Button
                        onClick={() => editInfo("users", data)}
                    >
                        <EditIcon size={20} />
                        Edit User Info
                    </Button>}
                </Flex>
                <Button onClick={onClose}>Close</Button>
            </Flex>
        </Content>
    );
};

export { UserDetails }